import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Snackbar,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  usuariosManutencaoApi,
  ManutencaoFilters,
  UserManutencao,
  UserManutencaoCreate,
  UserManutencaoUpdate,
  UserManutencaoSortDirection,
  UserManutencaoSortField,
} from '../api/usuariosManutencao'
import { gruposApi } from '../api/grupos'
import { empresasApi } from '../api/empresas'
import { useAuth } from '../contexts/AuthContext'
import { useLayout } from '../contexts/LayoutContext'
import { NivelAcesso } from '../types/auth'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ClearButton from '../components/ClearButton'
import ActionBar from '../components/ActionBar'
import UsuarioManutencaoForm from '../components/UsuariosManutencao/UsuarioManutencaoForm'
import UsuarioManutencaoViewDialog from '../components/UsuariosManutencao/UsuarioManutencaoViewDialog'
import VincularEmpresasDialog from '../components/UsuariosManutencao/VincularEmpresasDialog'
import AlterarSenhasDialog from '../components/UsuariosManutencao/AlterarSenhasDialog'

const UsuariosManutencaoPage: React.FC = () => {
  const { user } = useAuth()
  const { sidebarOpen } = useLayout()
  const queryClient = useQueryClient()
  /** Grid da barra de filtros: quando o menu está colapsado há mais largura, mais colunas; quando expandido, menos colunas. */
  const filterGridSize = sidebarOpen ? { xs: 12 as const, sm: 12 as const, md: 6 as const, lg: 2 as const } : { xs: 12 as const, sm: 6 as const, md: 2 as const }
  
  const [filters, setFilters] = useState<ManutencaoFilters>({
    skip: 0,
    limit: 25,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [sortField, setSortField] = useState<UserManutencaoSortField>('id')
  const [sortDirection, setSortDirection] = useState<UserManutencaoSortDirection>('asc')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedUsuario, setSelectedUsuario] = useState<UserManutencao | null>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [vincularEmpresasDialogOpen, setVincularEmpresasDialogOpen] = useState(false)
  const [alterarSenhasDialogOpen, setAlterarSenhasDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const isSuperAdmin = user?.nivel_acesso === NivelAcesso.SUPER_ADMIN
  const isAdmin = user?.nivel_acesso === NivelAcesso.ADMINISTRADOR
  // Pode criar: Super Admin e Admin
  const canCreate = isSuperAdmin || isAdmin
  // Pode editar: Super Admin, Admin, Gerente, Supervisor (backend valida escopo)
  const canEdit = user?.nivel_acesso !== undefined && user.nivel_acesso <= NivelAcesso.SUPERVISOR

  // Buscar grupos para filtro (apenas super admin)
  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos', 'filtro'],
    queryFn: () => gruposApi.list({ includeInactive: true }),
    enabled: isSuperAdmin,
  })

  // Buscar empresas para filtro (admin e super admin)
  const { data: empresas = [] } = useQuery({
    queryKey: ['empresas', 'filtro'],
    queryFn: () => empresasApi.list(),
    enabled: isSuperAdmin || isAdmin,
  })

  // Função auxiliar para atualizar filtros e resetar página
  const updateFilters = (newFilters: Partial<ManutencaoFilters>) => {
    setPage(0)
    setFilters(prev => ({ ...prev, ...newFilters, skip: 0 }))
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['usuarios-manutencao', { ...filters, sortField, sortDirection }],
    queryFn: () =>
      usuariosManutencaoApi.list({
        ...filters,
        sort_by: sortField,
        sort_direction: sortDirection,
      }),
  })

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      usuariosManutencaoApi.toggleStatus(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      setSnackbar({ open: true, message: 'Status atualizado com sucesso', severity: 'success' })
      setAnchorEl(null)
    },
    onError: (error: any) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Erro ao atualizar status', 
        severity: 'error' 
      })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: UserManutencaoCreate) => usuariosManutencaoApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      setSnackbar({ open: true, message: 'Usuário criado com sucesso', severity: 'success' })
      setFormDialogOpen(false)
    },
    onError: (error: any) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Erro ao criar usuário', 
        severity: 'error' 
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserManutencaoUpdate }) =>
      usuariosManutencaoApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      setSnackbar({ open: true, message: 'Usuário atualizado com sucesso', severity: 'success' })
      setFormDialogOpen(false)
    },
    onError: (error: any) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Erro ao atualizar usuário', 
        severity: 'error' 
      })
    },
  })

  const desvincularGrupoMutation = useMutation({
    mutationFn: ({ usuarioId, grupoId }: { usuarioId: number; grupoId: number }) =>
      usuariosManutencaoApi.desvincularGrupo(usuarioId, grupoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      setSnackbar({ open: true, message: 'Grupo desvinculado com sucesso', severity: 'success' })
      setAnchorEl(null)
    },
    onError: (error: any) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Erro ao desvincular grupo', 
        severity: 'error' 
      })
    },
  })

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, usuario: UserManutencao) => {
    setAnchorEl(event.currentTarget)
    setSelectedUsuario(usuario)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    // Não limpar selectedUsuario aqui - será limpo quando o dialog fechar
  }

  const handleToggleStatus = (active: boolean) => {
    if (selectedUsuario) {
      toggleStatusMutation.mutate({ id: selectedUsuario.id, active })
      setAnchorEl(null) // Fechar menu sem limpar selectedUsuario
    }
  }

  const handleView = () => {
    if (selectedUsuario) {
      setViewDialogOpen(true)
      setAnchorEl(null) // Fechar menu sem limpar selectedUsuario
    }
  }

  const handleEdit = async () => {
    if (selectedUsuario) {
      setAnchorEl(null) // Fechar menu
      // Buscar dados atualizados do servidor antes de abrir o formulário
      try {
        // Invalidar cache primeiro para garantir dados frescos
        queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao', selectedUsuario.id] })
        const usuarioAtualizado = await usuariosManutencaoApi.get(selectedUsuario.id)
        setSelectedUsuario(usuarioAtualizado)
        setFormMode('edit')
        setFormDialogOpen(true)
      } catch (error: any) {
        // Erro já é logado pelo React Query via onError
        // Se falhar, usar dados da lista mesmo (pode estar desatualizado)
      setFormMode('edit')
      setFormDialogOpen(true)
        if (error.response?.status !== 404) {
          setSnackbar({ 
            open: true, 
            message: error.response?.data?.detail || 'Erro ao carregar dados do usuário. Usando dados da lista.', 
            severity: 'error' 
          })
        }
      }
    }
  }

  const handleCreate = () => {
    setSelectedUsuario(null)
    setFormMode('create')
    setFormDialogOpen(true)
  }

  const handleVincularEmpresas = () => {
    if (selectedUsuario) {
      setVincularEmpresasDialogOpen(true)
      setAnchorEl(null) // Fechar menu sem limpar selectedUsuario
    }
  }

  const handleDesvincularGrupo = (grupoId: number) => {
    if (selectedUsuario) {
      if (window.confirm('Tem certeza que deseja desvincular este grupo? Apenas super administradores podem fazer isso.')) {
        desvincularGrupoMutation.mutate({ usuarioId: selectedUsuario.id, grupoId })
        setAnchorEl(null) // Fechar menu
      }
    }
  }

  const handleFormSubmit = (data: UserManutencaoCreate | UserManutencaoUpdate) => {
    if (formMode === 'create') {
      createMutation.mutate(data as UserManutencaoCreate)
    } else if (selectedUsuario) {
      updateMutation.mutate({ id: selectedUsuario.id, data: data as UserManutencaoUpdate })
    }
  }

  const getNivelBadgeColor = (nivel: number | null) => {
    if (nivel === null) return 'default'
    switch (nivel) {
      case 0: return 'error' // Super Admin
      case 1: return 'primary' // Admin
      case 2: return 'success' // Gerente
      case 3: return 'warning' // Supervisor
      default: return 'default'
    }
  }

  const getNivelName = (nivel: number | null) => {
    if (nivel === null) return 'N/A'
    switch (nivel) {
      case 0: return 'Super Admin'
      case 1: return 'Admin'
      case 2: return 'Gerente'
      case 3: return 'Supervisor'
      case 4: return 'Operador'
      case 5: return 'Padrão'
      default: return `Nível ${nivel}`
    }
  }

  const sortableColumnsBeforeGroups: Array<{ key: UserManutencaoSortField; label: string; align?: 'left' | 'right' | 'center' }> = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome' },
    { key: 'email', label: 'Email' },
    { key: 'nivel_acesso', label: 'Nível' },
    { key: 'login_thor', label: 'ID Thor/Login Thor' },
    { key: 'descricao_usuario', label: 'Descrição Usuário' },
  ]

  const sortableColumnsAfterGroups: Array<{ key: UserManutencaoSortField; label: string; align?: 'left' | 'right' | 'center' }> = [
    { key: 'is_active', label: 'Status' },
  ]

  const handleSort = (field: UserManutencaoSortField) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
    } else {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    }
    setPage(0)
    setFilters(prev => ({ ...prev, skip: 0 }))
  }

  const getSortDirection = (field: UserManutencaoSortField) => {
    if (sortField !== field) return 'asc'
    return sortDirection
  }
  
  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome' },
    { key: 'email', label: 'Email' },
    { key: 'nivel_acesso', label: 'Nível', format: (v: any) => getNivelName(v) },
    { key: 'iduser_thor', label: 'ID Thor' },
    { key: 'login_thor', label: 'Login Thor' },
    { key: 'descricao_usuario', label: 'Descrição Usuário' },
    { key: 'grupos', label: 'Grupos', format: (v: any) => Array.isArray(v) ? v.map((g: any) => g.nome).join(', ') : '' },
    { key: 'empresas', label: 'Empresas', format: (v: any) => Array.isArray(v) ? v.length.toString() : '0' },
    { key: 'is_active', label: 'Status', format: (v: any) => v ? 'Ativo' : 'Inativo' },
  ], [])

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Erro ao carregar usuários: {(error as Error).message}</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        py: { xs: 1.5, md: 2 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        minWidth: 0,
        transition: 'padding 0.3s ease',
        boxSizing: 'border-box',
      }}
    >
      {/* Bloco fixo ao rolar: mesmo limite de largura que o corpo (tabela) */}
      <Box
        component="section"
        aria-label="Barra de botões e barra de filtros"
        sx={{
          position: 'sticky',
          top: '4rem',
          zIndex: 10,
          flexShrink: 0,
          bgcolor: 'background.default',
          pb: 1,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Barra de botões (Excel, Criar Usuário) */}
        <ActionBar>
          <ExportButton
            data={data?.usuarios || []}
            columns={exportColumns}
            filename="usuarios_manutencao.xlsx"
            sheetName="Usuários"
          />
          {canCreate && (
            <AddButton onClick={handleCreate} title="Criar Usuário" />
          )}
        </ActionBar>

        {/* Barra de filtros (responsivo: viewport + estado do menu; colapsar menu libera mais colunas) */}
        <Paper component="section" aria-label="Barra de filtros" sx={{ p: { xs: 1, sm: 1.5 }, mb: 0 }}>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          <Grid item {...filterGridSize}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por email"
              value={filters.email || ''}
              onChange={(e) => updateFilters({ email: e.target.value || undefined })}
            />
          </Grid>
          <Grid item {...filterGridSize}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nome"
              value={filters.nome || ''}
              onChange={(e) => updateFilters({ nome: e.target.value || undefined })}
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item {...filterGridSize}>
              <FormControl fullWidth size="small">
                <InputLabel>Grupo</InputLabel>
                <Select
                  value={filters.grupo_id || ''}
                  label="Grupo"
                  onChange={(e) =>
                    updateFilters({
                      grupo_id: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {grupos.map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      {grupo.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {(isSuperAdmin || isAdmin) && (
            <Grid item {...filterGridSize}>
              <FormControl fullWidth size="small">
                <InputLabel>Empresa</InputLabel>
                <Select
                  value={filters.empresa_id || ''}
                  label="Empresa"
                  onChange={(e) =>
                    updateFilters({
                      empresa_id: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                >
                  <MenuItem value="">Todas</MenuItem>
                  {empresas.map((empresa) => (
                    <MenuItem key={empresa.id} value={empresa.id}>
                      {empresa.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item {...filterGridSize}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_active === undefined ? '' : filters.is_active}
                label="Status"
                onChange={(e) =>
                  updateFilters({
                    is_active: e.target.value === '' ? undefined : e.target.value === 'true',
                  })
                }
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Ativo</MenuItem>
                <MenuItem value="false">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item {...filterGridSize} sx={{ display: 'flex', alignItems: 'center' }}>
            <ClearButton
              title="Limpar filtros"
              onClick={() => {
                setFilters({ skip: 0, limit: 25 })
                setPage(0)
                setRowsPerPage(25)
                setSortField('id')
                setSortDirection('asc')
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      </Box>

      {/* Corpo: expande na altura para preencher a tela; tabela usa o espaço restante */}
      <Paper
        component="section"
        aria-label="Corpo da listagem"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          mt: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Área de dados: preenche a altura disponível; scroll interno na tabela */}
        <TableContainer
          sx={{
            width: '100%',
            maxWidth: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            bgcolor: 'background.paper',
            boxSizing: 'border-box',
            scrollbarGutter: 'stable',
          }}
        >
          <Table stickyHeader aria-label="Tabela de usuários" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {sortableColumnsBeforeGroups.map((column) => {
                  const isActive = sortField === column.key
                  return (
                    <TableCell
                      key={column.key}
                      align={column.align ?? 'left'}
                      sortDirection={isActive ? sortDirection : false}
                    >
                      <TableSortLabel
                        active={isActive}
                        direction={getSortDirection(column.key)}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  )
                })}
                <TableCell>Grupos</TableCell>
                <TableCell>Empresas</TableCell>
                {sortableColumnsAfterGroups.map((column) => {
                  const isActive = sortField === column.key
                  return (
                    <TableCell
                      key={column.key}
                      align={column.align ?? 'left'}
                      sortDirection={isActive ? sortDirection : false}
                    >
                      <TableSortLabel
                        active={isActive}
                        direction={getSortDirection(column.key)}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  )
                })}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : data?.usuarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Nenhum usuário encontrado
                  </TableCell>
                </TableRow>
              ) : (
                data?.usuarios.map((usuario) => {
                  const idThor = usuario.iduser_thor || '-'
                  const loginThor = usuario.login_thor || '-'
                  const thorInfo = `${idThor}/${loginThor}`
                  const semDadosThor = !usuario.iduser_thor || !usuario.login_thor
                  
                  return (
                    <TableRow key={usuario.id}>
                      <TableCell>{usuario.id}</TableCell>
                      <TableCell>{usuario.name}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={getNivelName(usuario.nivel_acesso)}
                          color={getNivelBadgeColor(usuario.nivel_acesso) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: semDadosThor ? 'warning.main' : 'text.primary',
                            fontWeight: semDadosThor ? 'bold' : 'normal'
                          }}
                        >
                          {thorInfo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {usuario.descricao_usuario || '-'}
                        </Typography>
                      </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {usuario.grupos.map((grupo) => (
                          <Chip key={grupo.id} label={grupo.nome} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {usuario.empresas.map((empresa) => (
                          <Chip key={empresa.id} label={empresa.nome} size="small" variant="outlined" color="primary" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={usuario.is_active ? 'Ativo' : 'Inativo'}
                        color={usuario.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, usuario)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          aria-label="Barra de paginação"
          count={data?.total || 0}
          page={page}
          onPageChange={(_, newPage) => {
            setPage(newPage)
            setFilters(prev => ({ ...prev, skip: newPage * rowsPerPage }))
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10)
            setRowsPerPage(newRowsPerPage)
            setPage(0)
            setFilters(prev => ({ ...prev, skip: 0, limit: newRowsPerPage }))
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              gap: 0.5,
              minHeight: { xs: 52, sm: 48 },
              paddingLeft: { xs: 1, sm: 2 },
              paddingRight: { xs: 1, sm: 2 },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              marginBottom: { xs: 0.5, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Menu de Ações */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedUsuario && (
          <MenuItem onClick={handleView}>
            <VisibilityIcon sx={{ mr: 1 }} /> Visualizar
          </MenuItem>
        )}
        {canEdit && selectedUsuario && (
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} /> Editar
          </MenuItem>
        )}
        {canEdit && selectedUsuario && (
          <MenuItem onClick={handleVincularEmpresas}>
            <EditIcon sx={{ mr: 1 }} /> Vincular/Desvincular Empresas
          </MenuItem>
        )}
        {selectedUsuario?.is_active ? (
          <MenuItem onClick={() => handleToggleStatus(false)}>
            <BlockIcon sx={{ mr: 1 }} /> Desativar
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleToggleStatus(true)}>
            <CheckCircleIcon sx={{ mr: 1 }} /> Ativar
          </MenuItem>
        )}
        {isSuperAdmin && selectedUsuario && (
          <>
            <MenuItem onClick={() => {
              setAlterarSenhasDialogOpen(true)
              handleMenuClose()
            }}>
              <EditIcon sx={{ mr: 1 }} /> Alterar Senhas
            </MenuItem>
            {selectedUsuario.grupos.map((grupo) => (
              <MenuItem 
                key={grupo.id}
                onClick={() => handleDesvincularGrupo(grupo.id)} 
                sx={{ color: 'error.main' }}
              >
                <BlockIcon sx={{ mr: 1 }} /> Desvincular Grupo: {grupo.nome}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Dialogs */}
      <UsuarioManutencaoViewDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedUsuario(null) // Limpar ao fechar dialog
        }}
        usuario={selectedUsuario}
      />

      <UsuarioManutencaoForm
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false)
          setSelectedUsuario(null)
        }}
        onSubmit={handleFormSubmit}
        usuario={selectedUsuario}
        loading={createMutation.isPending || updateMutation.isPending}
        mode={formMode}
      />

      <VincularEmpresasDialog
        open={vincularEmpresasDialogOpen}
        onClose={() => {
          setVincularEmpresasDialogOpen(false)
          setSelectedUsuario(null) // Limpar ao fechar dialog
        }}
        usuario={selectedUsuario}
      />

      <AlterarSenhasDialog
        open={alterarSenhasDialogOpen}
        onClose={() => {
          setAlterarSenhasDialogOpen(false)
        }}
        usuario={selectedUsuario}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UsuariosManutencaoPage

